import { defineComponent as _defineComponent } from 'vue'
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { ref, onMounted } from 'vue';
import axios from 'axios';

interface Props {
  lebYm: string;
  workInfoData: any[];  // hh_emp_sabok_work_info 테이블 데이터
  sisulCode: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'WageReportExcel',
  props: {
    lebYm: {},
    workInfoData: {},
    sisulCode: {}
  },
  emits: ["showSnackbar"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const props = __props;

const emit = __emit;

// 서비스 상세 데이터 저장
const serviceDetailsMap = ref<Record<string, any>>({});

// 만나이 계산 함수
const calculateAge = (birthDateStr: string, baseYm: string) => {
  if (!birthDateStr || !baseYm) return 0;

  const birthDate = new Date(birthDateStr);
  const baseYear = parseInt(baseYm.substring(0, 4));
  const baseMonth = parseInt(baseYm.substring(4, 6));
  const baseDate = new Date(baseYear, baseMonth, 0);

  let age = baseDate.getFullYear() - birthDate.getFullYear();
  if (
    baseDate.getMonth() < birthDate.getMonth() ||
    (baseDate.getMonth() === birthDate.getMonth() && baseDate.getDate() < birthDate.getDate())
  ) {
    age--;
  }
  return age;
};

// 근속기간 계산 함수
const calculateWorkPeriod = (startDateStr: string, baseYm: string) => {
  if (!startDateStr || !baseYm) return { years: 0, months: 0 };

  const workStartDate = new Date(startDateStr);
  const baseYear = parseInt(baseYm.substring(0, 4));
  const baseMonth = parseInt(baseYm.substring(4, 6));
  const baseDate = new Date(baseYear, baseMonth - 1, 1);

  let years = baseDate.getFullYear() - workStartDate.getFullYear();
  let months = baseDate.getMonth() - workStartDate.getMonth();

  if (months < 0) {
    years--;
    months += 12;
  }
  return { years, months };
};

// 근속년수 포맷팅 함수
const formatWorkPeriod = (years: number, months: number) => {
  if (years === 0 && months === 0) return '0개월';
  if (years === 0) return `${months}개월`;
  if (months === 0) return `${years}년`;
  return `${years}년 ${months}개월`;
};

// 생년월일을 YYYY-MM-DD 형식으로 맞춰주는 헬퍼
const normalizeBirthDate = (raw: string | undefined | null) => {
  if (!raw) return '';
  const str = String(raw).trim();
  if (!str) return '';
  if (str.includes('-')) return str;
  const digits = str.replace(/[^0-9]/g, '');
  if (digits.length === 8) {
    return `${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6, 8)}`;
  }
  return str;
};

// 직종별 정렬 순서
const getJobOrder = (jobType: string) => {
  if (jobType === '센터장') return 1;
  if (jobType === '사회복지사') return 2;
  if (jobType === '요양보호사') return 3;
  return 999;
};

// 서비스 상세 데이터 로드
const loadServiceDetails = async () => {
  if (!props.sisulCode || !props.lebYm) return;
  
  try {
    const response = await axios.get('/api/calc/service-summary', {
      params: {
        sisulCode: props.sisulCode,
        yearMonth: props.lebYm
      }
    });
    
    if (response.data && Array.isArray(response.data)) {
      const map: Record<string, any> = {};
      response.data.forEach((item: any) => {
        if (!map[item.emp_code]) {
          map[item.emp_code] = {
            workDays: item.work_days || 0,
            services: []
          };
        }
        map[item.emp_code].services.push({
          name: item.service_name,
          count: item.service_count || 0,
          hours: item.total_suga_hours || 0
        });
      });
      serviceDetailsMap.value = map;
    }
  } catch (error) {
    console.warn('서비스 상세 조회 실패 (무시):', error);
  }
};

// 임금대장 엑셀 다운로드 함수
const downloadPayrollReport = async () => {
  if (!props.workInfoData || props.workInfoData.length === 0) {
    emit('showSnackbar', "다운로드할 데이터가 없습니다.", "warning");
    return;
  }
  
  // 서비스 상세 데이터 로드
  await loadServiceDetails();

  try {
    const year = props.lebYm.substring(0, 4);
    const month = props.lebYm.substring(4, 6);
    // 2025년 이하 & 5월에만 근로자의날수당 별도 표시 (2026년부터는 일반 휴일수당에 포함)
    const isLaborDayMonth = month === '05' && parseInt(year) <= 2025;
    const isWelfarePayAvailable = true;  // 처우개선비 항상 표시

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('임금대장');

    // 열 인덱스 계산
    const basicInfoEnd = 4;
    const wageStart = basicInfoEnd + 1;
    // 임금구성: 기본급~휴일수당(6) + 장기근속~자녀교육비(9) + 기타수당(1) + 임금구성합계(1) = 17개
    // + 근로자의날수당(조건부 1) + 처우개선비(조건부 1)
    let wageColCount = 6 + 9 + 1 + 1;  // 기본 17개: 기본급~휴일(6) + 장기근속~자녀교육비(9) + 기타수당(1) + 합계(1)
    if (isLaborDayMonth) wageColCount += 1;  // 근로자의날수당
    if (isWelfarePayAvailable) wageColCount += 1;  // 처우개선비
    const wageEnd = wageStart + wageColCount - 1;
    const empDeductStart = wageEnd + 1;
    const empDeductEnd = empDeductStart + 10 - 1;  // 근로자공제 10개
    const centerDeductStart = empDeductEnd + 1;
    const centerDeductEnd = centerDeductStart + 8 - 1;  // 기관공제 8개
    const referenceStart = centerDeductEnd + 1;
    const referenceEnd = referenceStart + 5 - 1;  // 참고사항 5개       // 참고사항 5개 (실수령액, 비과세액, 실수령증가액, 인건비절감액, 기관수익)
    const workDetailStart = referenceEnd + 1;      // 근무내역 시작
    const workDetailEnd = workDetailStart + 4 - 1;     // 근무내역 4개 (서비스명, 횟수, 시간, 근로일)
    const remarkCol = workDetailEnd + 1;           // 비고 1개

    // 2단 헤더
    const header2Columns = [
      '순번', '성명', '생년월일', '직종',
      '기본급', '주휴수당', '연차수당', '연장수당', '야간수당', '휴일수당',
      ...(isLaborDayMonth ? ['근로자의날수당'] : []),
      '장기근속수당', '보수교육비', '중증수급자가산', '식대', '교통비', '교육비', '상여금', '차량보조비', '자녀교육비',
      ...(isWelfarePayAvailable ? ['처우개선비'] : []),
      '기타수당', '임금구성합계',
      '국민연금', '건강보험', '장기요양보험', '고용보험', '소득세', '지방세', '지원공제', '본인부담금공제', '기타공제', '근로자공제합계',
      '국민연금', '건강보험', '장기요양보험', '고용보험', '실업급여', '산재보험', '퇴직금', '기관공제합계',
      '실수령액', '비과세액', '실수령증가액', '인건비절감액', '기관수익',
      '서비스명', '횟수', '시간', '근로일', '비고'
    ];

    // 디버깅: 열 위치 출력
    console.log('=== 열 인덱스 디버깅 ===');
    console.log(`기본정보: 1~${basicInfoEnd}`);
    console.log(`임금구성: ${wageStart}~${wageEnd} (개수: ${wageColCount})`);
    console.log(`근로자공제: ${empDeductStart}~${empDeductEnd}`);
    console.log(`기관공제: ${centerDeductStart}~${centerDeductEnd}`);
    console.log(`참고사항: ${referenceStart}~${referenceEnd}`);
    console.log(`근무내역: ${workDetailStart}~${workDetailEnd}`);
    console.log(`비고: ${remarkCol}`);
    console.log(`전체 헤더 개수: ${header2Columns.length}`);
    console.log('헤더 내용:', header2Columns);

    // 1단 헤더 행 추가
    const header1Row = worksheet.addRow([]);
    header1Row.getCell(1).value = '기본정보';
    header1Row.getCell(wageStart).value = '임금구성';
    header1Row.getCell(empDeductStart).value = '근로자공제';
    header1Row.getCell(centerDeductStart).value = '기관공제';
    header1Row.getCell(referenceStart).value = '참고사항';
    header1Row.getCell(workDetailStart).value = '근무내역';
    header1Row.getCell(remarkCol).value = '비고';

    // 1단 헤더 병합
    worksheet.mergeCells(1, 1, 1, basicInfoEnd);
    worksheet.mergeCells(1, wageStart, 1, wageEnd);
    worksheet.mergeCells(1, empDeductStart, 1, empDeductEnd);
    worksheet.mergeCells(1, centerDeductStart, 1, centerDeductEnd);
    worksheet.mergeCells(1, referenceStart, 1, referenceEnd);      // 참고사항 5개 (실수령액~기관수익)
    worksheet.mergeCells(1, workDetailStart, 1, workDetailEnd);    // 근무내역 4개 (서비스명, 횟수, 시간, 근로일)
    // 비고는 별도 (병합 없음)

    // 1단 헤더 스타일
    for (let i = 1; i <= header2Columns.length; i++) {
      const cell = header1Row.getCell(i);
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4472C4' } };
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 12 };
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
      cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
    }

    // 2단 헤더 행 추가
    const header2Row = worksheet.addRow(header2Columns);
    header2Row.eachCell((cell, colNumber) => {
      const isWorkDetailArea = colNumber >= workDetailStart && colNumber <= workDetailEnd;  // 근무내역만
      const isRemarkArea = colNumber === remarkCol;  // 비고는 별도
      
      let bgColor = 'FFD9E1F2';  // 기본색
      if (isWorkDetailArea) bgColor = 'FFFDE68A';  // 근무내역은 노란색
      if (isRemarkArea) bgColor = 'FFE2EFDA';  // 비고는 연두색
      
      cell.fill = { 
        type: 'pattern', 
        pattern: 'solid', 
        fgColor: { argb: bgColor }
      };
      cell.font = { bold: true, size: 11 };
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
      cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
    });

    // 열 너비 설정
    worksheet.getColumn(1).width = 6;
    worksheet.getColumn(2).width = 10;
    worksheet.getColumn(3).width = 12;
    worksheet.getColumn(4).width = 12;
    for (let i = 5; i <= header2Columns.length; i++) {
      const colName = header2Columns[i - 1];
      if (colName === '중증수급자가산') worksheet.getColumn(i).width = 16;
      else if (colName === '근로자의날수당') worksheet.getColumn(i).width = 15;
      else if (colName === '장기요양보험') worksheet.getColumn(i).width = 16;
      else if (colName.includes('합계')) worksheet.getColumn(i).width = 14;
      else if (colName === '실수령증가액' || colName === '인건비절감액') worksheet.getColumn(i).width = 14;
      else if (colName === '서비스명') worksheet.getColumn(i).width = 50;
      else if (colName === '횟수' || colName === '시간') worksheet.getColumn(i).width = 8;
      else if (colName === '근로일') worksheet.getColumn(i).width = 10;
      else if (colName === '비고') worksheet.getColumn(i).width = 18;
      else worksheet.getColumn(i).width = 12;
    }

    // 합계 변수
    let totals = {
      basicPay: 0, weeklyPay: 0, annualPay: 0, overtimePay: 0, nightPay: 0, holidayPay: 0,
      laborDayPay: 0, longServicePay: 0, bosuEduPay: 0, severeCareAddition: 0, mealPay: 0, transportationFee: 0,
      educationFee: 0, bonus: 0, vehicleAllowance: 0, childEducationFee: 0, welfarePay: 0, otherPay: 0, wageSum: 0,
      empPension: 0, empHealth: 0, empLongTermCare: 0, empEmployment: 0,
      empIncomeTax: 0, empLocalTax: 0, empSupportDeduction: 0, empBoninPay: 0, empEtc: 0, empTotal: 0,
      centerPension: 0, centerHealth: 0, centerLongTermCare: 0, centerEmployment: 0,
      centerUnemployment: 0, centerIndustrialAccident: 0, centerRetirement: 0, centerTotal: 0,
      netPay: 0, nonTaxable: 0, employeeSavings: 0, centerSavings: 0, centerProfit: 0
    };

    // 데이터 정렬 (센터장 > 사회복지사 > 요양보호사)
    const sortedData = [...props.workInfoData].sort((a, b) => {
      const orderA = getJobOrder(a.emp_job);
      const orderB = getJobOrder(b.emp_job);
      if (orderA !== orderB) return orderA - orderB;
      return (a.emp_name || '').localeCompare(b.emp_name || '');
    });

    // 데이터 행 추가
    sortedData.forEach((row: any, index: number) => {
      // DB에서 가져온 값 그대로 사용
      const basicPay = row.ewi_basic_pay || 0;
      const weeklyPay = row.ewi_weekly_pay || 0;
      const annualPay = row.ewi_annual_pay || 0;
      const overtimePay = row.ewi_overtime_pay || 0;
      const nightPay = row.ewi_night_pay || 0;
      const holidayPay = row.ewi_holiday_pay || 0;
      const laborDayPay = row.ewi_labor_day_pay || 0;
      const longServicePay = row.ewi_long_service_pay || 0;
      const bosuEduPay = row.ewi_bosu_edu_pay || 0;
      const severeCareAddition = row.ewi_severe_care_addition || 0;
      const mealPay = row.ewi_meal_pay || 0;
      const transportationFee = row.ewi_transportation_fee || 0;
      const educationFee = row.ewi_education_fee || 0;
      const bonus = row.ewi_bonus || 0;
      const vehicleAllowance = row.ewi_vehicle_allowance || 0;
      const childEducationFee = row.ewi_child_education_fee || 0;
      const welfarePay = row.ewi_welfare_pay || 0;
      const otherPay = row.ewi_other_pay || 0;
      const wageSum = row.ewi_total_pay || 0;

      // 근로자 공제 (DB 값 그대로)
      const empPension = row.ewi_emp_yeon || 0;
      const empHealth = row.ewi_emp_gun || 0;
      const empLongTermCare = row.ewi_emp_jang || 0;
      const empEmployment = row.ewi_emp_go || 0;
      const empIncomeTax = row.ewi_emp_so || 0;
      const empLocalTax = row.ewi_emp_ji || 0;
      const empSupportDeduction = row.ewi_emp_support_deduction || 0;
      const empBoninPay = row.ewi_emp_bonin_pay || 0;
      const empEtc = row.ewi_emp_etc || 0;
      const empTotal = row.ewi_emp_total_deduction || 0;

      // 기관 공제 (DB 값 그대로)
      const centerPension = row.ewi_center_yeon || 0;
      const centerHealth = row.ewi_center_gun || 0;
      const centerLongTermCare = row.ewi_center_jang || 0;
      const centerEmployment = row.ewi_center_go || 0;
      const centerUnemployment = row.ewi_center_sil || 0;
      const centerIndustrialAccident = row.ewi_center_san || 0;
      const centerRetirement = row.ewi_center_out || 0;
      const centerTotal = row.ewi_center_total_deduction || 0;

      // 참고사항 (숫자 타입 보장)
      const netPay = Number(row.ewi_net_pay) || 0;
      const nonTaxable = Number(row.ewi_non_taxable_amount) || 0;
      const employeeSavings = Number(row.ewi_emp_tax_savings) || 0;
      const centerSavings = Number(row.ewi_center_tax_savings) || 0;
      const centerProfit = Number(row.ewi_center_profit) || 0;

      // 만나이, 근속기간 계산
      const birthDate = normalizeBirthDate(row.emp_birth);
      const age = birthDate ? calculateAge(birthDate, props.lebYm) : 0;
      const workPeriod = row.emp_in_date ? calculateWorkPeriod(row.emp_in_date, props.lebYm) : { years: 0, months: 0 };
      const remarkText = `만 ${age}세 / ${formatWorkPeriod(workPeriod.years, workPeriod.months)}`;
      
      // 근무내역: 서비스 상세 정보 (서비스명, 횟수, 시간을 각각 별도 열로)
      let serviceNamesVal: any = '';
      let serviceCountsVal: any = '';
      let serviceHoursVal: any = '';
      let workDaysVal: any = '';
      
      const empCode = row.emp_code;
      const serviceDetail = serviceDetailsMap.value[empCode];
      if (serviceDetail) {
        const workDays = Number(serviceDetail.workDays) || 0;
        
        if (serviceDetail.services && serviceDetail.services.length > 0) {
          const validServices = serviceDetail.services.filter((svc: any) => svc.name);
          
          console.log(`[${row.emp_name}] 서비스 개수: ${validServices.length}`, validServices);
          
          if (validServices.length === 1) {
            // 서비스 1개: 숫자 타입으로 저장
            serviceNamesVal = validServices[0].name;
            serviceCountsVal = Number(validServices[0].count) || 0;
            serviceHoursVal = Number(validServices[0].hours) || 0;
          } else if (validServices.length > 1) {
            // 서비스 여러개: 줄바꿈 문자열
            serviceNamesVal = validServices.map((svc: any) => svc.name).join('\n');
            serviceCountsVal = validServices.map((svc: any) => svc.count).join('\n');
            serviceHoursVal = validServices.map((svc: any) => svc.hours).join('\n');
            
            console.log(`[${row.emp_name}] 서비스명:`, serviceNamesVal);
            console.log(`[${row.emp_name}] 횟수:`, serviceCountsVal);
            console.log(`[${row.emp_name}] 시간:`, serviceHoursVal);
          }
        }
        
        if (workDays > 0) {
          workDaysVal = workDays;  // 숫자 타입 그대로
        }
      }

      // 행 데이터
      const rowData = [
        index + 1,
        row.emp_name || '',
        birthDate,
        row.emp_job || '',
        basicPay, weeklyPay, annualPay, overtimePay, nightPay, holidayPay,
        ...(isLaborDayMonth ? [laborDayPay] : []),
        longServicePay, bosuEduPay, severeCareAddition, mealPay, transportationFee, educationFee, bonus, vehicleAllowance, childEducationFee,
        ...(isWelfarePayAvailable ? [welfarePay] : []),
        otherPay, wageSum,
        empPension, empHealth, empLongTermCare, empEmployment, empIncomeTax, empLocalTax, empSupportDeduction, empBoninPay, empEtc, empTotal,
        centerPension, centerHealth, centerLongTermCare, centerEmployment, centerUnemployment, centerIndustrialAccident, centerRetirement, centerTotal,
        netPay, nonTaxable, employeeSavings, centerSavings, centerProfit,
        serviceNamesVal, serviceCountsVal, serviceHoursVal, workDaysVal, remarkText
      ];

      const dataRow = worksheet.addRow(rowData);
      
      // 서비스 개수에 따라 행 높이 조절 (이미 위에서 선언된 serviceDetail 재사용)
      if (serviceDetail && serviceDetail.services && serviceDetail.services.length > 0) {
        const validServices = serviceDetail.services.filter((svc: any) => svc.name);
        if (validServices.length > 1) {
          // 여러 서비스인 경우 행 높이 증가 (줄당 20 높이 + 기본 5)
          dataRow.height = Math.max(20, validServices.length * 18);
        }
      }

      // 데이터 행 스타일
      dataRow.eachCell((cell, colNumber) => {
        if (colNumber <= basicInfoEnd) {
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF2F2F2' } };
          cell.alignment = { horizontal: 'center', vertical: 'middle' };
        } else if (colNumber <= wageEnd) {
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE7F3E7' } };
          cell.alignment = { horizontal: 'right', vertical: 'middle' };
          cell.numFmt = '#,##0';
          if (colNumber === wageEnd) cell.font = { bold: true };
        } else if (colNumber <= empDeductEnd) {
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFE7E7' } };
          cell.alignment = { horizontal: 'right', vertical: 'middle' };
          cell.numFmt = '#,##0';
          if (colNumber === empDeductEnd) cell.font = { bold: true };
        } else if (colNumber <= centerDeductEnd) {
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF4E7' } };
          cell.alignment = { horizontal: 'right', vertical: 'middle' };
          cell.numFmt = '#,##0';
          if (colNumber === centerDeductEnd) cell.font = { bold: true };
        } else if (colNumber <= referenceEnd) {
          // 참고사항 (실수령액, 비과세액, 실수령증가액, 인건비절감액, 기관수익)
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE7F0FF' } };
          cell.alignment = { horizontal: 'right', vertical: 'middle' };
          cell.numFmt = '#,##0';
        } else if (colNumber >= workDetailStart && colNumber <= workDetailEnd) {
          // 근무내역 (서비스명, 횟수, 시간, 근로일)
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFEF3C7' } };
          const isServiceName = colNumber === workDetailStart;  // 첫 번째 열이 서비스명
          const isServiceCount = colNumber === workDetailStart + 1;  // 두 번째 열이 횟수
          const isServiceHours = colNumber === workDetailStart + 2;  // 세 번째 열이 시간
          
          cell.alignment = { 
            horizontal: isServiceName ? 'left' : 'right',
            vertical: 'middle',
            wrapText: true  // 모든 근무내역 열에 줄바꿈 적용
          };
          // 횟수, 시간, 근로일은 숫자 포맷 적용 (서비스명 제외)
          if (!isServiceName) {
            cell.numFmt = '#,##0';
          }
        } else if (colNumber === remarkCol) {
          // 비고 (별도 영역)
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF2F2F2' } };  // 회색 배경
          cell.alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };
        } else {
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE7F0FF' } };
          cell.alignment = { horizontal: 'right', vertical: 'middle' };
          cell.numFmt = '#,##0';
        }
        cell.border = {
          top: { style: 'thin', color: { argb: 'FFD0D0D0' } },
          left: { style: 'thin', color: { argb: 'FFD0D0D0' } },
          bottom: { style: 'thin', color: { argb: 'FFD0D0D0' } },
          right: { style: 'thin', color: { argb: 'FFD0D0D0' } }
        };
      });

      // 합계 누적
      totals.basicPay += basicPay;
      totals.weeklyPay += weeklyPay;
      totals.annualPay += annualPay;
      totals.overtimePay += overtimePay;
      totals.nightPay += nightPay;
      totals.holidayPay += holidayPay;
      totals.laborDayPay += laborDayPay;
      totals.longServicePay += longServicePay;
      totals.bosuEduPay += bosuEduPay;
      totals.severeCareAddition += severeCareAddition;
      totals.mealPay += mealPay;
      totals.transportationFee += transportationFee;
      totals.educationFee += educationFee;
      totals.bonus += bonus;
      totals.vehicleAllowance += vehicleAllowance;
      totals.childEducationFee += childEducationFee;
      if (isWelfarePayAvailable) totals.welfarePay += welfarePay;
      totals.otherPay += otherPay;
      totals.wageSum += wageSum;
      totals.empPension += empPension;
      totals.empHealth += empHealth;
      totals.empLongTermCare += empLongTermCare;
      totals.empEmployment += empEmployment;
      totals.empIncomeTax += empIncomeTax;
      totals.empLocalTax += empLocalTax;
      totals.empSupportDeduction += empSupportDeduction;
      totals.empBoninPay += empBoninPay;
      totals.empEtc += empEtc;
      totals.empTotal += empTotal;
      totals.centerPension += centerPension;
      totals.centerHealth += centerHealth;
      totals.centerLongTermCare += centerLongTermCare;
      totals.centerEmployment += centerEmployment;
      totals.centerUnemployment += centerUnemployment;
      totals.centerIndustrialAccident += centerIndustrialAccident;
      totals.centerRetirement += centerRetirement;
      totals.centerTotal += centerTotal;
      totals.netPay += netPay;
      totals.nonTaxable += nonTaxable;
      totals.employeeSavings += employeeSavings;
      totals.centerSavings += centerSavings;
      totals.centerProfit += centerProfit;
    });

    // 합계 행 추가
    const totalRowData = [
      '', '합계', '', '',
      totals.basicPay, totals.weeklyPay, totals.annualPay, totals.overtimePay, totals.nightPay, totals.holidayPay,
      ...(isLaborDayMonth ? [totals.laborDayPay] : []),
      totals.longServicePay, totals.bosuEduPay, totals.severeCareAddition, totals.mealPay, totals.transportationFee, totals.educationFee, totals.bonus, totals.vehicleAllowance, totals.childEducationFee,
      ...(isWelfarePayAvailable ? [totals.welfarePay] : []),
      totals.otherPay, totals.wageSum,
      totals.empPension, totals.empHealth, totals.empLongTermCare, totals.empEmployment,
      totals.empIncomeTax, totals.empLocalTax, totals.empSupportDeduction, totals.empBoninPay, totals.empEtc, totals.empTotal,
      totals.centerPension, totals.centerHealth, totals.centerLongTermCare, totals.centerEmployment,
      totals.centerUnemployment, totals.centerIndustrialAccident, totals.centerRetirement, totals.centerTotal,
      totals.netPay, totals.nonTaxable, totals.employeeSavings, totals.centerSavings, totals.centerProfit,
      '', '', '', '', ''  // 서비스명, 횟수, 시간, 근로일, 비고
    ];

    const totalRow = worksheet.addRow(totalRowData);
    totalRow.eachCell((cell, colNumber) => {
      // 참고사항(실수령액~기관수익)까지 포함하여 숫자 컬럼 처리
      const isNumericColumn = colNumber > basicInfoEnd && colNumber <= referenceEnd;

      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF2CC' } };
      cell.font = { bold: true, size: 11 };
      cell.alignment = { 
        horizontal: isNumericColumn ? 'right' : 'center', 
        vertical: 'middle' 
      };
      cell.border = { top: { style: 'medium' }, left: { style: 'thin' }, bottom: { style: 'medium' }, right: { style: 'thin' } };
      // 숫자 컬럼에 천단위 구분 쉼표 적용
      if (isNumericColumn) {
        cell.numFmt = '#,##0';
      }
    });

    // 파일 다운로드
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const fileName = `임금대장_${year}년${month}월.xlsx`;
    saveAs(blob, fileName);

    emit('showSnackbar', "임금대장 다운로드 완료", "success");
  } catch (error) {
    console.error('엑셀 다운로드 오류:', error);
    emit('showSnackbar', "임금대장 다운로드 중 오류가 발생했습니다.", "error");
  }
};

// 외부에서 호출 가능하도록 expose
__expose({
  downloadPayrollReport
});

return () => {}
}

})